import { ChangeEvent, useState } from 'react'
import { usePreferences } from './PreferencesProvider'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, Switch, Typography } from '@mui/material'
import { PreferencesConfig, defaultPreferences } from '../../utils/Preferences'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseIcon from '@mui/icons-material/Close'

const BRAND_GREEN = '#042930'

const Strings = {
  Title: 'Configuración de cookies',
  Message:
    'Utilizamos cookies propias y de terceros para que el sitio y la aplicación funcionen correctamente. Puedes aceptar todas las cookies o gestionarlas por categoría.',

  Necessary: 'Necesarias',
  NecessaryMessage:
    'Son esenciales para el funcionamiento del sitio y la aplicación y no pueden desactivarse. Permiten:',
  NecessaryChecklist: [
    'Guardar tus preferencias de privacidad',
    'Mantener tu sesión iniciada',
    'Habilitar funciones básicas de la app',
    'Proteger la seguridad y prevenir el fraude',
  ],

  AlwaysActive: 'Siempre activas',

  Analytics: 'Analíticas',
  AnalyticsMessage:
    'Nos ayudan a entender cómo se usa el sitio para mejorar el servicio. La información es agregada y no identifica usuarios. Herramientas utilizadas:',

  Marketing: 'Marketing',
  MarketingMessage:
    'Se utilizan para medir conversiones y mostrarte publicidad relevante en plataformas de terceros. Herramientas utilizadas:',

  AcceptAll: 'Aceptar todas',
  AcceptNecessary: 'Solo necesarias',
  Save: 'Guardar',
}

const analytics = [
  'Google Analytics (uso del sitio)',
  'Microsoft Clarity (mapas de calor y sesiones)',
  'Vercel Analytics (uso general)',
  'Vercel Speed Insight (rendimiento)',
]

const marketing = ['Google Ads (conversiones y remarketing)']

// Estilos compartidos por los 3 acordeones: garantizan que expand icon,
// altura y el elemento de la derecha (Chip/Switch) queden en la misma posición.
const accordionSx = {
  borderRadius: "10px",
  overflow: 'hidden',
  '&:before': { display: 'none' },
  '&:not(.Mui-expanded)': { height: 48 },
}

const summarySx = {
  flexDirection: 'row-reverse',
  alignItems: 'center',
  minHeight: 48,
  '&.Mui-expanded': { minHeight: 48 },

  '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': {
    margin: '12px 0',
  },

  '& .MuiAccordionSummary-expandIconWrapper': {
    mr: 1.5,
  },

  '& .MuiAccordionSummary-content': {
    marginLeft: 1,
  },
} as const

const headerRowSx = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  pr: 1,
} as const

export function PreferencesDialog() {
  const ctx = usePreferences()
  return (
    <Dialog
      onClose={() => ctx.setDialogOpen(false)}
      open={ctx.isDialogOpen}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 4, maxHeight: '80dvh' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="h5" fontWeight={600}>
          {Strings.Title}
        </Typography>

        <IconButton onClick={() => ctx.setDialogOpen(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <PreferencesForm />
      </DialogContent>
    </Dialog>
  )
}

function PreferencesForm() {
  const ctx = usePreferences()
  const [draft, setDraft] = useState<PreferencesConfig>(ctx.preferences ?? defaultPreferences)

  const togglePreference =
    (name: keyof PreferencesConfig) => (event: ChangeEvent<HTMLInputElement>, value: boolean) => {
      setDraft((prev) => ({ ...prev, [name]: value }))
    }

  const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation()

  const buttonSx = {
    borderRadius: 2,
    bgcolor: '#C9C9CF',
    color: BRAND_GREEN,
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
      bgcolor: '#afafb6',
    },
  }

  const switchSx = {
    ml: 'auto',
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: BRAND_GREEN,
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: BRAND_GREEN,
    },
  }

  return (
    <Box
      component="form"
      sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
      onSubmit={(e) => {
        e.preventDefault()
        ctx.saveConfig(draft)
      }}
    >
      <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
        {Strings.Message}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Accordion square disableGutters variant="outlined" sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            <Box sx={headerRowSx}>
              <Typography fontWeight={600} sx={{ color: BRAND_GREEN }}>
                {Strings.Necessary}
              </Typography>
              <Chip
                label={Strings.AlwaysActive}
                size="small"
                sx={{ ml: 'auto', bgcolor: BRAND_GREEN, color: 'white' }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              {Strings.NecessaryMessage}
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, fontSize: 13, color: 'text.secondary' }}>
              {Strings.NecessaryChecklist.map((text, index) => (
                <li key={index}>{text}</li>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion square disableGutters variant="outlined" sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            <Box sx={headerRowSx}>
              <Typography fontWeight={600} sx={{ color: BRAND_GREEN }}>
                {Strings.Analytics}
              </Typography>
              <Switch
                size="small"
                sx={switchSx}
                checked={draft.analytics}
                onChange={togglePreference('analytics')}
                onClick={stopPropagation}
                onFocus={stopPropagation}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              {Strings.AnalyticsMessage}
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, fontSize: 13, color: 'text.secondary' }}>
              {analytics.map((text, index) => (
                <li key={index}>{text}</li>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion square disableGutters variant="outlined" sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            <Box sx={headerRowSx}>
              <Typography fontWeight={600} sx={{ color: BRAND_GREEN }}>
                {Strings.Marketing}
              </Typography>
              <Switch
                size="small"
                sx={switchSx}
                checked={draft.marketing}
                onChange={togglePreference('marketing')}
                onClick={stopPropagation}
                onFocus={stopPropagation}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              {Strings.MarketingMessage}
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2, fontSize: 13, color: 'text.secondary' }}>
              {marketing.map((text, index) => (
                <li key={index}>{text}</li>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          gap: 1.5,
          mt: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 1.5,
          }}
        >
          <Button onClick={ctx.acceptAll} sx={buttonSx}>
            {Strings.AcceptAll}
          </Button>

          <Button onClick={ctx.acceptNecessary} sx={buttonSx}>
            {Strings.AcceptNecessary}
          </Button>
        </Box>

        <Button variant="contained" type="submit" sx={buttonSx}>
          {Strings.Save}
        </Button>
      </Box>
    </Box>
  )
}